import { PageBanner, Section, useDocument } from '@nanning/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage } from '../useSiteData';
import type { ImageryDoc } from '../types';

interface Bi {
  zh: string;
  en: string;
}
interface PublicationsDoc {
  /** useDocument 要求带 _meta；完整性由 check-consistency 的 R8 保证 */
  _meta: any;
  lead: Bi;
  intro: Bi;
  items: { id: string; type: string; date: string; title: Bi; summary: Bi; body: Bi[] }[];
}

/** 集团刊物：年度信、年度回顾、社会责任摘要与集团数据一览，全文呈现。 */
export function Publications() {
  const s = useDocument<PublicationsDoc>('group', 'publications.json');
  const imagery = useDocument<ImageryDoc>('group', 'imagery.json');
  const { t, pick } = useT();

  if (s.error) {
    return <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{s.error}</div>;
  }
  if (!s.data) {
    return <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>;
  }
  const d = s.data;
  const img = imagery.data ? findImage(imagery.data, 'skyline') : null;

  return (
    <>
      <PageBanner
        image={img ? assetUrl(img.local) : undefined}
        imageAlt={img ? pick(img.alt) : undefined}
        imageCredit={img ? creditOf(img) : undefined}
        eyebrow={t('pub.eyebrow')}
        title={t('pub.title')}
        lead={pick(d.lead)}
      />

      <Section id="intro" tone="surface">
        <p className="max-w-3xl text-base leading-relaxed text-body">{pick(d.intro)}</p>
      </Section>

      <Section id="items">
        <div className="flex flex-col gap-20">
          {d.items.map((it) => (
            <article key={it.id} id={it.id} className="border-t border-line pt-12">
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
                <span className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 text-primary-light">
                  {t(`pub.type.${it.type}`)}
                </span>
                <span className="tnum">{it.date}</span>
              </div>
              <h2 className="mt-5 text-2xl font-semibold leading-snug text-body md:text-4xl">{pick(it.title)}</h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-primary-light">{pick(it.summary)}</p>
              <div className="mt-8 grid max-w-3xl gap-6">
                {it.body.map((para, i) => (
                  <p key={i} className="text-base leading-relaxed text-muted">
                    {pick(para)}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
        <p className="mt-20 max-w-3xl text-xs leading-relaxed text-muted">{t('pub.notice')}</p>
      </Section>
    </>
  );
}
